import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon, type PhxIconName } from '@/shared/ui/icon';

export type AlertSeverity = 'info' | 'success' | 'warning' | 'error';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  severity: AlertSeverity;
  title?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}

const SEVERITY: Record<
  AlertSeverity,
  { icon: PhxIconName; container: string; icon_bg: string; title: string }
> = {
  info: {
    icon: 'info',
    container: 'border-phx-info bg-phx-info-tint text-phx-info-text',
    icon_bg: 'text-phx-info',
    title: 'text-phx-info-text',
  },
  success: {
    icon: 'ok',
    container: 'border-phx-success bg-phx-success-tint text-phx-success-text',
    icon_bg: 'text-phx-success',
    title: 'text-phx-success-text',
  },
  warning: {
    icon: 'alert',
    container: 'border-phx-warning bg-phx-warning-tint text-phx-warning-text',
    icon_bg: 'text-phx-warning',
    title: 'text-phx-warning-text',
  },
  error: {
    icon: 'alert',
    container: 'border-phx-danger bg-phx-danger-tint text-phx-danger-text',
    icon_bg: 'text-phx-danger',
    title: 'text-phx-danger-text',
  },
};

/**
 * Inline feedback component for contextual messages bound to screen content.
 * For transient system notifications use the `Toast` system instead.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { severity, title, children, action, className, ...rest },
  ref
) {
  const cfg = SEVERITY[severity];
  return (
    <div
      ref={ref}
      role="alert"
      data-severity={severity}
      className={cn(
        'phx-alert gap-phx-3 rounded-phx-md px-phx-4 py-phx-3 flex items-start border border-l-[3px]',
        cfg.container,
        className
      )}
      {...rest}
    >
      <Icon name={cfg.icon} size={14} className={cn('mt-[2px] shrink-0', cfg.icon_bg)} />
      <div className={cn('min-w-0 flex-1', action && 'gap-phx-4 flex items-start justify-between')}>
        <div className="min-w-0">
          {title ? (
            <div className={cn('text-phx-md mb-phx-1 font-semibold', cfg.title)}>{title}</div>
          ) : null}
          <div className="text-phx-sm leading-relaxed">{children}</div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
});
